<template>
  <div id="divergencia-dados-indicadores">
    <Menu />

    <div class="container main">
      <div class="row">
        <div class="col-md-12">
          <SectionTitle
            title="Divergência Dados Indicadores"
            description="Dados divergentes entre Prontuário Eletrônico e SIAPS"
            url="/indicadores-aps"
            :enabledModalInformacao="true"
            :enabledIframeModalInformacao="true"
            urlIframeModalInformacao="https://doc.clickup.com/9011510980/d/h/8cj1fp4-1631/599ea88448ce3d9/8cj1fp4-9811"
            pageIdentifier="divergencia-dados-indicadores"
          />
        </div>
      </div>

      <ValidationObserver ref="form">
        <div class="row mb-3">
          <div class="col-md-5">
            <validation-provider v-slot="{ errors }" rules="required">
              <label for="">Indicadores</label>
              <vue-select
                :options="indicadoresOptions"
                label="label"
                placeholder=""
                v-model="form.indicador"
                :disabled="loading"
              >
                <template slot="option" slot-scope="option">
                  {{ option.label }}
                </template>
                <div slot="no-options">Não há opções correspondentes</div>
              </vue-select>
              <div class="error">{{ errors[0] }}</div>
            </validation-provider>
          </div>
          <div class="col-md-3">
            <validation-provider v-slot="{ errors }" rules="required">
              <label for="">Meses</label>

              <vue-select
                :options="monthOptions"
                label="label"
                placeholder="Selecione os meses"
                v-model="form.mes"
                :clearable="false"
                :multiple="true"
                :selectable="() => form.mes.length < 4"
                :disabled="loading"
              >
                <template slot="option" slot-scope="option">
                  {{ option.label }}
                </template>

                <div slot="no-options">Não há opções correspondentes</div>
              </vue-select>

              <small class="text-muted">
                {{ form.mes.length }} de 4 meses selecionados
              </small>

              <div class="error">{{ errors[0] }}</div>
            </validation-provider>
          </div>
          <div class="col-md-2">
            <validation-provider
              v-slot="{ errors }"
              rules="required|onlyNumbers|ano"
            >
              <label for="">Ano</label>
              <input
                type="text"
                class="form-control"
                placeholder=""
                v-model="form.ano"
                v-inputmask="maskNumbers"
                maxlength="4"
                :disabled="loading"
              />
              <div class="error">{{ errors[0] }}</div>
            </validation-provider>
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <b-button variant="secondary" :disabled="loading" @click="exportar">
              <template v-if="loading">
                <b-spinner small class="mr-2" />
                Exportando
                {{ Math.min(progresso.atual + 1, progresso.total) }}
                de {{ progresso.total }}
              </template>

              <template v-else>
                <i class="icofont-download mr-1"></i>
                Exportar
              </template>
            </b-button>
          </div>
        </div>
      </ValidationObserver>
    </div>

    <Footer />
  </div>
</template>

<script>
import Menu from '@/components/Menu'
import Footer from '@/components/Footer'
import SectionTitle from '@/components/SectionTitle'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { validations } from '@/mixins/validations'
import Masks from '@/mixins/masks'
validations()

export default {
  name: 'DivergenciaDadosIndicadores',
  components: {
    Menu,
    Footer,
    SectionTitle,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      indicadoresOptions: [
        { label: 'Tratamento Restaurador Atraumático', value: 41 },
        { label: 'Exodontias', value: 39 },
        { label: 'Procedimentos Odontológicos Preventivos', value: 40 },
        { label: 'Escovação supervisionada', value: 42 },
        { label: 'Primeira consulta', value: 38 },
        { label: 'Tratamentos concluídos', value: 43 },
      ],
      monthOptions: [
        { label: 'Janeiro', value: '01' },
        { label: 'Fevereiro', value: '02' },
        { label: 'Março', value: '03' },
        { label: 'Abril', value: '04' },
        { label: 'Maio', value: '05' },
        { label: 'Junho', value: '06' },
        { label: 'Julho', value: '07' },
        { label: 'Agosto', value: '08' },
        { label: 'Setembro', value: '09' },
        { label: 'Outubro', value: '10' },
        { label: 'Novembro', value: '11' },
        { label: 'Dezembro', value: '12' },
      ],
      form: {
        indicador: null,
        mes: [],
        ano: null,
      },
      loading: false,
      progresso: {
        atual: 0,
        total: 0,
      },
    }
  },
  computed: {
    maskNumbers() {
      return Masks['number']
    },
  },
  methods: {
    /**
     * Exporta os dados divergentes dos meses selecionados.
     *
     * Executa as requisições sequencialmente, realizando
     * o download de um arquivo Excel para cada mês.
     *
     * @returns {Promise<void>}
     */
    async exportar() {
      if (this.loading) {
        return
      }

      const isValid = await this.$refs.form.validate()

      if (!isValid) {
        return
      }

      const meses = this.form.mes
        .map((mes) => Number(mes.value))
        .sort((a, b) => a - b)

      if (meses.length < 1 || meses.length > 4) {
        this.$toast.warning('Selecione entre 1 e 4 meses.', '', {
          position: 'topRight',
        })
        return
      }

      this.loading = true

      this.progresso = {
        atual: 0,
        total: meses.length,
      }

      const indicador = this.form.indicador.label
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')

      const ano = Number(this.form.ano)

      let sucessos = 0
      let erros = 0

      try {
        for (const mes of meses) {
          try {
            const payload = {
              indicador: this.form.indicador.value,
              mes,
              ano,
            }

            const response = await this.$api.post(
              `/app/divergencia-dados-indicadores/${this.$store.state.cod_ibge}/exportar`,
              payload,
              {
                responseType: 'blob',
              }
            )

            const nomeArquivo =
              [
                'divergencia-dados-indicadores',
                indicador,
                String(mes).padStart(2, '0'),
                ano,
              ].join('-') + '.xls'

            const blob = new Blob([response.data], {
              type: 'application/vnd.ms-excel',
            })

            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')

            try {
              link.href = url
              link.download = nomeArquivo

              document.body.appendChild(link)
              link.click()
            } finally {
              link.remove()
              window.URL.revokeObjectURL(url)
            }

            sucessos++
          } catch (error) {
            erros++

            console.error(`Erro ao exportar divergências do mês ${mes}:`, error)
          } finally {
            this.progresso.atual++
          }
        }

        if (erros === 0) {
          this.$toast.success(
            `${sucessos} arquivo(s) exportado(s) com sucesso.`,
            '',
            { position: 'topRight' }
          )
        } else {
          this.$toast.warning(
            `${sucessos} arquivo(s) exportado(s). ${erros} falharam.`,
            '',
            { position: 'topRight' }
          )
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss"></style>
